import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import {
  type Avatar,
  createUserAvatar,
  deleteAvatarByUserId,
  updateUserAvatar,
} from '@/api/cms/avatars';
import { getActiveAvatar } from '@/helpers/avatar';
import { saveUserData } from '@/helpers/userData';
import { fetcherInternal } from '@/modules/api';
import { useGetCommonStore } from '@/store';

const DEFAULT_AVATAR = [
  {
    avatarId: null,
    avatarImg: null,
    avatarCmsFieldId: null,
    id: null,
  },
];

export const useCMSAvatar = () => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const [isMutating, setIsMutating] = useState(false);

  const swrKey = user?.id ? '/cms/avatars' : null;

  const {
    data = DEFAULT_AVATAR,
    error,
    isLoading,
    mutate: mutateAvatar,
  } = useSWRImmutable<Avatar[]>(swrKey, fetcherInternal);

  const { avatarId, avatarImg, avatarCmsFieldId } = useMemo(() => {
    const userAvatar = data && data.length > 0 ? data[0] : DEFAULT_AVATAR[0];
    const avatarId = userAvatar.avatarId;

    return {
      avatarId,
      avatarImg: getActiveAvatar(avatarId).avatar,
      avatarCmsFieldId: userAvatar.id,
    };
  }, [data]);

  useEffect(() => {
    const avatarId = data?.[0]?.avatarId;
    if (avatarId !== null) {
      saveUserData({ avatarId });
    }
  }, [data]);

  const changeAvatar = useCallback(
    async (newAvatarId: number) => {
      if (!user?.id) return;
      setIsMutating(true);

      const payload = { avatarId: newAvatarId };

      try {
        if (avatarCmsFieldId) {
          const previousData = data;
          mutateAvatar(
            data.map((item) => ({
              ...item,
              avatarId: newAvatarId,
            })) as Avatar[],
            false
          );
          try {
            await updateUserAvatar(avatarCmsFieldId, payload);
            await mutateAvatar();
          } catch (err) {
            mutateAvatar(previousData as Avatar[], false);
            throw err;
          }
        } else {
          await createUserAvatar(payload);
          await mutateAvatar();
        }

        saveUserData({ avatarId: newAvatarId });
      } finally {
        setIsMutating(false);
      }
    },
    [user?.id, avatarCmsFieldId, data, mutateAvatar]
  );

  const deleteAvatar = async () => {
    if (!avatarCmsFieldId) return;
    setIsMutating(true);

    await deleteAvatarByUserId(avatarCmsFieldId);
    await mutateAvatar();
    saveUserData({ avatarId: null });

    setIsMutating(false);
  };

  return {
    avatarId,
    avatarImg,
    avatarCmsFieldId,
    isLoading,
    isError: !!error,
    mutateAvatar,
    changeAvatar,
    deleteAvatar,
    isMutating,
  };
};
